import * as Tabs from 'primitives/tabs'
import * as React from 'react'
import { Typography } from '../typography'

import { APITable } from './api-table'

type TabType = 'preview' | 'api' | 'changelog' | 'design'

interface APIProperty {
  name: string
  type: string
  defaultValue: string
  description: string
}

interface DemoTabsProps {
  tabs: {
    preview: React.ReactNode
    api: {
      properties: APIProperty[]
    }
    changelog: Array<{
      date: string
      changes: string[]
    }>
    design?: React.ReactNode
  }
  defaultValue?: TabType
  className?: string
}

const TAB_CONFIG: Array<{ value: TabType; label: string }> = [
  { value: 'preview', label: 'Preview' },
  { value: 'api', label: 'API' },
  { value: 'design', label: 'Design Guidelines' },
  { value: 'changelog', label: 'Changelog' },
]

export function DemoTabs({
  tabs,
  defaultValue = 'preview',
  className = 'space-y-6',
}: DemoTabsProps) {
  const renderContent = (type: TabType) => {
    switch (type) {
      case 'preview': {
        return tabs.preview
      }
      case 'api': {
        return (
          <div className="border-cf-grey-100 rounded-lg border p-6">
            <Typography variant="cf-heading-03" className="text-cf-grey-900 mb-4">
              API Reference
            </Typography>
            <APITable properties={tabs.api.properties} />
          </div>
        )
      }
      case 'changelog': {
        return (
          <div className="border-cf-grey-100 rounded-lg border p-6">
            <Typography variant="cf-heading-03" className="text-cf-grey-900 mb-4">
              Changelog
            </Typography>
            <div className="space-y-8">
              {tabs.changelog.map((entry, index) => (
                <div key={index}>
                  <div className="flex items-center gap-2 mb-3">
                    <Typography variant="cf-heading-03" className="text-cf-grey-900">
                      {entry.date}
                    </Typography>
                  </div>
                  <ul className="list-disc pl-4 space-y-2 text-cf-grey-700">
                    {entry.changes.map((change, changeIndex) => (
                      <li key={changeIndex}>{change}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )
      }
      case 'design': {
        return <div className="h-[90vh]">{tabs.design}</div>
      }
    }
  }

  const filteredTabConfig = TAB_CONFIG.filter(tab => {
    if (tab.value !== 'design') {
      return true
    }
    // Only show 'design' tab if tabs.design is defined and not empty
    return tabs.design !== undefined && tabs.design !== null && tabs.design !== ''
  })

  return (
    <Tabs.Root defaultValue={defaultValue} className={className}>
      <Tabs.List className="border-b border-cf-grey-200">
        {filteredTabConfig.map(tab => (
          <Tabs.Trigger
            key={tab.value}
            value={tab.value}
            className="data-[state=active]:border-cf-grey-900 data-[state=active]:text-cf-grey-900 -mb-px border-b-2 border-transparent px-6 py-3 text-cf-grey-600 hover:text-cf-grey-900"
          >
            {tab.label}
          </Tabs.Trigger>
        ))}
      </Tabs.List>

      {filteredTabConfig.map(tab => (
        <Tabs.Content key={tab.value} value={tab.value} className="outline-none">
          {renderContent(tab.value as TabType)}
        </Tabs.Content>
      ))}
    </Tabs.Root>
  )
}
